import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { ChannelSession } from './ChannelSession';
import type { RealtimeEvent, RealtimeEventDefinition } from './eventDefinition';
import type {
  RealtimeParticipant,
  RealtimeParticipantIdentity,
  RealtimePresence,
  RealtimePresenceDefinition,
} from './presenceDefinition';
import type { PresenceUser } from './protocol';
import { useRealtimeServiceInternal } from './context';
import type { ChannelState, ConnectionState } from './types';

const EMPTY_PRESENCE = Object.freeze([]) as unknown as PresenceUser[];
const noop = (): void => {};
const readState = (session: ChannelSession): ChannelState => session.getStateSnapshot();
const readPresence = (session: ChannelSession): PresenceUser[] => session.getPresenceSnapshot();

function toIdentity(participant: PresenceUser, ownConnectionId: string | null): RealtimeParticipantIdentity {
  return {
    userId: participant.user_id,
    connectionId: participant.connection_id,
    isSelf: participant.connection_id === ownConnectionId,
  };
}

/**
 * Read one of a session's two snapshots without ever holding the session across a commit.
 *
 * Both callbacks resolve the session by key at the moment they run: `subscribe` runs in the commit
 * and is the call that creates the session and acquires its lease, while `getSnapshot` only ever
 * looks one up. A session dropped between render and commit therefore cannot leave a consumer
 * wired to an object the registry has moved on from.
 */
function useChannelStore<T>(channelKey: string | null, read: (session: ChannelSession) => T, fallback: T): T {
  const service = useRealtimeServiceInternal();

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (channelKey === null) return noop;
      return service.channel(channelKey).subscribeStore(onStoreChange);
    },
    [channelKey, service],
  );

  const getSnapshot = useCallback(() => {
    if (channelKey === null) return fallback;
    const session = service.findChannel(channelKey);
    return session ? read(session) : fallback;
  }, [channelKey, fallback, read, service]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The connection id alone rather than the whole connection state, so a presence consumer does not
 * re-render on every connecting/reconnecting transition.
 */
function useOwnConnectionId(): string | null {
  const service = useRealtimeServiceInternal();
  return useSyncExternalStore(service.subscribe, service.getConnectionIdSnapshot, service.getConnectionIdSnapshot);
}

/** The channel's raw presence document plus the id needed to tell our own entry apart. */
function usePresenceSource(channelKey: string | null): [PresenceUser[], string | null] {
  const presence = useChannelStore(channelKey, readPresence, EMPTY_PRESENCE);
  return [presence, useOwnConnectionId()];
}

/** Observe the complete connection lifecycle without exposing the service that owns it. */
export function useRealtimeStatus(): ConnectionState {
  const service = useRealtimeServiceInternal();
  return useSyncExternalStore(service.subscribe, service.getSnapshot, service.getSnapshot);
}

/** Register one typed, fan-out event interest on a context-object channel. */
export function useRealtimeEvent<T>(
  channelKey: string | null,
  definition: RealtimeEventDefinition<T>,
  handler: (event: RealtimeEvent<T>) => void,
): void {
  const service = useRealtimeServiceInternal();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const eventName = definition.name;
  useEffect(() => {
    if (channelKey === null) return;
    return service.channel(channelKey).listen<T>(eventName, (event) => handlerRef.current(event));
  }, [channelKey, eventName, service]);
}

/** A state observer is an interest and therefore drives the shared channel subscription. */
export function useRealtimeChannelState(channelKey: string | null): ChannelState {
  return useChannelStore<ChannelState>(channelKey, readState, 'unsubscribed');
}

/**
 * Who is on the channel, without decoding any slice. For a roster that needs identities only; a
 * feature reading its own metadata wants `useRealtimePresence` instead.
 */
export function useRealtimeParticipants(channelKey: string | null): RealtimeParticipantIdentity[] {
  const [presence, ownConnectionId] = usePresenceSource(channelKey);

  return useMemo(
    () => presence.map((participant) => toIdentity(participant, ownConnectionId)),
    [ownConnectionId, presence],
  );
}

/**
 * Observe one typed presence slice and own an optional local contribution to it. The contribution
 * is removed automatically when this hook changes channel/definition or unmounts.
 */
export function useRealtimePresence<T>(
  channelKey: string | null,
  definition: RealtimePresenceDefinition<T>,
): RealtimePresence<T> {
  const service = useRealtimeServiceInternal();
  const [presence, ownConnectionId] = usePresenceSource(channelKey);
  const ownerRef = useRef<symbol>();
  if (!ownerRef.current) ownerRef.current = Symbol(`presence:${definition.name}`);
  const owner = ownerRef.current;

  const name = definition.name;
  const legacyProjection = definition.legacyProjection;

  useEffect(() => {
    if (channelKey === null) return;
    // `findChannel`, not `channel`: tearing this hook down must not resurrect a session.
    return () => service.findChannel(channelKey)?.setPresenceSlice(owner, name, null, legacyProjection);
  }, [channelKey, legacyProjection, name, owner, service]);

  const setPresence = useCallback(
    (data: T | null) => {
      if (channelKey === null) return;
      service.channel(channelKey).setPresenceSlice(owner, name, data, legacyProjection);
    },
    [channelKey, legacyProjection, name, owner, service],
  );

  const participants = useMemo<RealtimeParticipant<T>[]>(
    () =>
      presence.map((participant) => {
        let data: T | null = null;
        try {
          data = definition.decode(participant.data);
        } catch (error) {
          console.error(`[Realtime] Failed to decode presence slice "${name}"`, error);
        }
        return { ...toIdentity(participant, ownConnectionId), data };
      }),
    [definition, name, ownConnectionId, presence],
  );

  return useMemo(() => ({ participants, setPresence }), [participants, setPresence]);
}
